import { useEffect, useRef } from "react"

import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"

// A light-speed starfield on a transparent canvas: while `active`, white
// streaks rush outward from a vanishing point, accelerating for the first few
// seconds up to full warp; once inactive they coast to a stop and fade. The
// 2D-canvas take on the "hyperspace" effect, kept to a few dozen lines so it
// can sit inside a small card; no WebGL, no colour.
const STAR_COUNT = 240
const SPREAD = 1.3 // how far stars start from the axis, in projected units
const IDLE_SPEED = 0.05 // depth units per second while coasting to a halt
const WARP_SPEED = 7.6 // depth units per second at full warp
const WARP_IN_MS = 6000 // time to accelerate from rest to full warp
const WARP_OUT_MS = 900
const STREAK_LENGTH = 0.12 // fraction of a star's depth drawn as its tail

interface Star {
  x: number
  y: number
  z: number
}

function randomStar(depth = Math.random()): Star {
  return {
    x: (Math.random() * 2 - 1) * SPREAD,
    y: (Math.random() * 2 - 1) * SPREAD,
    z: Math.max(depth, 0.02),
  }
}

interface HyperspaceCanvasProps {
  active: boolean
  className?: string
  /** Vanishing point as a fraction of the canvas size. */
  originX?: number
  originY?: number
}

export function HyperspaceCanvas({
  active,
  className,
  originX = 0.5,
  originY = 0.5,
}: HyperspaceCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const activeRef = useRef(active)
  const warpRef = useRef(0)
  const frameRef = useRef<number | null>(null)
  const lastTimeRef = useRef<number | null>(null)
  const starsRef = useRef<Star[] | null>(null)
  const requestDrawRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    activeRef.current = active
    lastTimeRef.current = null
    requestDrawRef.current?.()
  }, [active])

  const prefersReducedMotion = usePrefersReducedMotion()
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    if (prefersReducedMotion) return

    const canvasElement = canvas
    const stars = (starsRef.current ??= Array.from({ length: STAR_COUNT }, () => randomStar()))

    function requestDraw() {
      if (frameRef.current !== null) return
      frameRef.current = window.requestAnimationFrame(draw)
    }
    requestDrawRef.current = requestDraw

    function draw(timestamp: number) {
      frameRef.current = null
      // Leave the canvas untouched until the effect first runs: sizing it (or
      // creating its context) while idle repaints the card on load, which
      // reads as the artwork underneath blinking.
      if (!activeRef.current && warpRef.current === 0) return
      const context = canvasElement.getContext("2d")
      if (!context) return

      const previousTime = lastTimeRef.current ?? timestamp
      const dt = Math.min(64, timestamp - previousTime) / 1000
      lastTimeRef.current = timestamp

      // Spin up with a steady ramp over WARP_IN_MS (speed follows warp², so the
      // streaks keep accelerating the whole way); settle with a quick ease-out.
      const warp = activeRef.current
        ? Math.min(1, warpRef.current + (dt * 1000) / WARP_IN_MS)
        : warpRef.current * (1 - Math.min(1, (dt * 1000) / WARP_OUT_MS))
      warpRef.current = warp

      const bounds = canvasElement.getBoundingClientRect()
      const pixelRatio = window.devicePixelRatio || 1
      const width = Math.max(1, Math.round(bounds.width * pixelRatio))
      const height = Math.max(1, Math.round(bounds.height * pixelRatio))
      if (canvasElement.width !== width || canvasElement.height !== height) {
        canvasElement.width = width
        canvasElement.height = height
      }
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
      context.clearRect(0, 0, bounds.width, bounds.height)

      if (warp < 0.004 && !activeRef.current) {
        warpRef.current = 0
        return
      }

      const cx = bounds.width * originX
      const cy = bounds.height * originY
      const focal = Math.min(bounds.width, bounds.height) * 0.9
      const speed = IDLE_SPEED + (WARP_SPEED - IDLE_SPEED) * warp * warp
      const tail = STREAK_LENGTH * (0.25 + warp)

      context.lineCap = "round"
      context.globalCompositeOperation = "lighter"

      for (let index = 0; index < stars.length; index += 1) {
        const star = stars[index]!
        const previousZ = star.z
        star.z -= speed * dt
        if (star.z <= 0.02) {
          stars[index] = randomStar(1)
          continue
        }
        const headScale = focal / star.z
        const tailZ = Math.min(1, previousZ + tail * star.z)
        const tailScale = focal / tailZ
        const hx = cx + star.x * headScale
        const hy = cy + star.y * headScale
        const tx = cx + star.x * tailScale
        const ty = cy + star.y * tailScale
        // Stars near the lens read brightest and thickest; distant ones are faint dust.
        const closeness = 1 - star.z
        const alpha = Math.min(1, (0.12 + closeness * 0.9) * (0.35 + warp * 0.65))
        context.strokeStyle = `rgba(255, 255, 255, ${alpha.toFixed(3)})`
        context.lineWidth = 0.4 + closeness * 1.4
        context.beginPath()
        context.moveTo(tx, ty)
        context.lineTo(hx, hy)
        context.stroke()
      }

      context.globalCompositeOperation = "source-over"
      requestDraw()
    }

    const resizeObserver = new ResizeObserver(requestDraw)
    resizeObserver.observe(canvasElement)
    requestDraw()

    return () => {
      resizeObserver.disconnect()
      requestDrawRef.current = null
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
    }
  }, [originX, originY, prefersReducedMotion])

  return (
    <canvas
      aria-hidden="true"
      ref={canvasRef}
      className={className}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
    />
  )
}
